import {
  useUserActiveOrganization,
  useOrganizationRename,
  useUserRole
} from "@/api";
import { Settings } from "@/components/ui/SettingsSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { useRouter } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { CircleX, Save } from "lucide-react";
import { ChangeEvent, useEffect, useState } from "react";

type Props = {
  setFlashNewSlug: (state: boolean) => void;
};

const RenameRow = ({ setFlashNewSlug }: Props) => {
  const [errorRenaming, setErrorRenaming] = useState(false);
  const [newOrganizationName, setNewOrganizationName] = useState("");

  const { navigate } = useRouter();
  const { activeOrganization, loading: loadingActiveOrganization } =
    useUserActiveOrganization();
  const [renameOrganization, { loading: renamingOrganization, error }] =
    useOrganizationRename();
  const { isAdmin } = useUserRole();
  const hasNameChanged =
    !loadingActiveOrganization &&
    newOrganizationName.trim() !== activeOrganization?.name;

  useEffect(() => {
    if (activeOrganization) setNewOrganizationName(activeOrganization?.name);
  }, [activeOrganization]);

  const handleNameValidation = ({
    target: { value }
  }: ChangeEvent<HTMLInputElement>) => {
    //* Regular expression to match only allowed characters
    const regex = /^[a-zA-Z0-9- "':;]*$/;
    if (regex.test(value)) {
      setNewOrganizationName(value);
      setErrorRenaming(false);
    } else {
      setErrorRenaming(true);
      setTimeout(() => {
        setErrorRenaming(false);
      }, 4000);
    }
  };

  const handleRenameSubmit = () => {
    if (!activeOrganization || !isAdmin) return;
    renameOrganization({
      variables: {
        id: activeOrganization.id,
        name: newOrganizationName.trim()
      }
    })
      .then((res) => {
        if (res.data?.organizationRename.__typename === "Organization") {
          navigate({
            to: "/organization/$organizationSlug/organization-settings",
            params: { organizationSlug: res.data?.organizationRename?.slug }
          });
          setFlashNewSlug(true);
          setTimeout(() => {
            setFlashNewSlug(false);
          }, 5000);
          toast({
            title: "Organization renamed",
            description: "Name and slug successfully changed."
          });
        }
      })
      .catch((err) => {
        console.error(err);
        toast({
          variant: "destructive",
          title: "Renaming failed",
          description:
            "There was an error renaming the organization - please try again."
        });
      });
  };

  return (
    <Settings.Item
      title="Organization name"
      description={
        <span
          data-testid="nameHelpText"
          className={
            errorRenaming || error
              ? "text-brand-error dark:text-brand-error"
              : "text-brand-gray-700 dark:text-brand-gray-200"
          }
        >
          Organization name may only consist of{" "}
          <i className="dark:text-brand-gray-100">
            <code>a-z</code>
          </i>
          ,{" "}
          <i className="dark:text-brand-gray-100">
            <code>A-Z</code>
          </i>
          ,{" "}
          <i className="dark:text-brand-gray-100">
            <code>0-9</code>
          </i>
          , <i className="dark:text-brand-gray-100">&quot; &quot;</i>, and the
          characters{" "}
          <i className="dark:text-brand-gray-100">
            <code>&apos;;:&quot;</code>
          </i>
          .
        </span>
      }
    >
      <div className="grid grid-cols-[1fr_72px] gap-2 md:gap-4 items-center">
        <Input
          data-testid="renameInput"
          type="text"
          id="name"
          value={newOrganizationName}
          onChange={(e) => handleNameValidation(e)}
          disabled={renamingOrganization || !isAdmin || !activeOrganization}
          className={cn(
            "min-w-36 text-sm bg-white dark:bg-brand-gray-800 text-brand-gray-900 dark:text-brand-gray-100 border border-brand-gray-200 dark:border-brand-gray-700",
            errorRenaming &&
              "focus-visible:ring-brand-error dark:focus-visible:ring-brand-error"
          )}
        />
        {hasNameChanged && (
          <motion.div
            key="saveClearBtns"
            className="flex flex-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Button
              variant="ghost"
              size="icon"
              data-testid="saveNameBtn"
              onClick={handleRenameSubmit}
              disabled={!newOrganizationName}
            >
              <Save size={16} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              data-testid="clearNameBtn"
              onClick={() =>
                activeOrganization &&
                setNewOrganizationName(activeOrganization.name)
              }
            >
              <CircleX size={16} />
            </Button>
          </motion.div>
        )}
      </div>
    </Settings.Item>
  );
};

export default RenameRow;
